import React, { type ReactNode, createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';
export type NotificationCategory = 'analyse' | 'mission' | 'systeme';

export interface NotificationItem {
    id: string;
    type: NotificationType;
    /** Auto-inferred from title when not explicitly provided */
    category: NotificationCategory;
    title: string;
    message?: string;
    timestamp: number;
    read: boolean;
    action?: {
        label: string;
        onClick?: () => void;
        /** Serializable navigation path — survives localStorage round-trip */
        path?: string;
    };
}

interface NotifyOptions {
    description?: string;
    action?: { label: string; onClick?: () => void; path?: string };
    duration?: number;
    /** Override auto-inferred category */
    category?: NotificationCategory;
}

interface NotificationContextType {
    notifications: NotificationItem[];
    unreadCount: number;
    notify: (type: NotificationType, title: string, options?: NotifyOptions) => void;
    markAsRead: (id: string) => void;
    markAllAsRead: () => void;
    clearAll: () => void;
    removeNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) throw new Error('useNotifications must be used within a NotificationProvider');
    return context;
};

/** Infer the notification category from its title when not explicitly provided. */
function inferCategory(title: string, explicit?: NotificationCategory): NotificationCategory {
    if (explicit) return explicit;
    const t = title.toLowerCase();
    if (t.includes('mission') || t.includes('doublon') || t.includes('kanban') || t.includes('colonne') || t.includes('invitation') || t.includes('membre') || t.includes('rôle') || t.includes('role')) return 'mission';
    if (t.includes('analyse') || t.includes('cv') || t.includes('profil') || t.includes('matching') || t.includes('pdf') || t.includes('envoi') || t.includes('talent') || t.includes('coordonnées') || t.includes('lien de partage')) return 'analyse';
    return 'systeme';
}

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
    const { user, org } = useAuth();

    // Seed from localStorage for instant first render (replaced by Supabase once loaded)
    const [notifications, setNotifications] = useState<NotificationItem[]>(() => {
        try {
            const saved = localStorage.getItem('revert_notifications');
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });

    const [supabaseLoaded, setSupabaseLoaded] = useState(false);

    const unreadCount = notifications.filter(n => !n.read).length;

    // Mirror to localStorage on every change (offline fallback)
    useEffect(() => {
        localStorage.setItem('revert_notifications', JSON.stringify(notifications));
    }, [notifications]);

    // Load from Supabase once user and org are known — replaces the localStorage seed
    useEffect(() => {
        if (!user?.id || !org?.id || supabaseLoaded) return;

        (supabase as any)
            .from('notifications')
            .select('id, type, category, title, message, read, action_label, action_path, created_at')
            .eq('user_id', user.id)
            .eq('org_id', org.id)
            .order('created_at', { ascending: false })
            .limit(50)
            .then(({ data, error }) => {
                if (error) {
                    console.warn('[Notifications] Supabase load failed:', error.message);
                    return;
                }
                if (!data) return;
                const items: NotificationItem[] = data.map(row => ({
                    id: row.id,
                    type: row.type as NotificationType,
                    category: (row.category ?? 'systeme') as NotificationCategory,
                    title: row.title,
                    message: row.message ?? undefined,
                    timestamp: new Date(row.created_at).getTime(),
                    read: row.read,
                    action: row.action_label
                        ? { label: row.action_label, path: row.action_path ?? undefined }
                        : undefined,
                }));
                setNotifications(items);
                setSupabaseLoaded(true);
            });
    }, [user?.id, org?.id, supabaseLoaded]);

    const notify = useCallback((type: NotificationType, title: string, options?: NotifyOptions) => {
        const id = crypto.randomUUID();
        const timestamp = Date.now();
        const category = inferCategory(title, options?.category);

        // 1. Ephemeral Sonner toast
        const actionForToast = options?.action
            ? { label: options.action.label, onClick: options.action.onClick ?? (() => {}) }
            : undefined;
        const toastOptions = { description: options?.description, action: actionForToast, duration: options?.duration };
        switch (type) {
            case 'success': toast.success(title, toastOptions); break;
            case 'error':   toast.error(title, toastOptions);   break;
            case 'info':    toast.info(title, toastOptions);    break;
            case 'warning': toast.warning(title, toastOptions); break;
        }

        // 2. Persistent local state
        const newItem: NotificationItem = {
            id, type, category, title,
            message: options?.description,
            timestamp, read: false,
            action: options?.action,
        };
        setNotifications(prev => [newItem, ...prev].slice(0, 50));

        // 3. Supabase persistence (fire-and-forget, non-blocking)
        if (user?.id) {
            (supabase as any).from('notifications').insert({
                id,
                user_id: user.id,
                org_id: org?.id ?? null,
                type,
                category,
                title,
                message: options?.description ?? null,
                action_label: options?.action?.label ?? null,
                action_path: options?.action?.path ?? null,
            }).then(({ error }: any) => {
                if (error) console.warn('[Notifications] Supabase insert failed:', error.message);
            });
        }
    }, [user?.id, org?.id]);

    const markAsRead = useCallback((id: string) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        if (user?.id) {
            (supabase as any).from('notifications').update({ read: true })
                .eq('id', id).eq('user_id', user.id)
                .then(({ error }: any) => { if (error) console.warn('[Notifications] markAsRead failed:', error.message); });
        }
    }, [user?.id]);

    const markAllAsRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        if (user?.id) {
            (supabase as any).from('notifications').update({ read: true })
                .eq('user_id', user.id).eq('read', false)
                .then(({ error }: any) => { if (error) console.warn('[Notifications] markAllAsRead failed:', error.message); });
        }
    }, [user?.id]);

    const clearAll = useCallback(() => {
        setNotifications([]);
        if (user?.id) {
            supabase.from('notifications').delete().eq('user_id', user.id)
                .then(({ error }) => { if (error) console.warn('[Notifications] clearAll failed:', error.message); });
        }
    }, [user?.id]);

    const removeNotification = useCallback((id: string) => {
        setNotifications(prev => prev.filter(n => n.id !== id));
        if (user?.id) {
            supabase.from('notifications').delete().eq('id', id).eq('user_id', user.id)
                .then(({ error }) => { if (error) console.warn('[Notifications] removeNotification failed:', error.message); });
        }
    }, [user?.id]);

    return (
        <NotificationContext.Provider value={{
            notifications, unreadCount, notify,
            markAsRead, markAllAsRead, clearAll, removeNotification,
        }}>
            {children}
        </NotificationContext.Provider>
    );
};
